import { CART_ID, upsellsRemovedKey } from "utils/constants";
import isBundleWithSubProducts, { isAddOn, getBundleSubproductLines } from "./bundle";
import extractIdFromGid from "helpers/extract-gid";

export const getExistingCartId = () => localStorage.getItem(CART_ID);

export const getOrCreateCartId = async (shopifyClient) => {
	const cartId = getExistingCartId();
	if (cartId) return cartId;

	const cart = await shopifyClient.cart.create();

	localStorage.setItem(CART_ID, cart.id);

	return cart.id;
};

export const mapCart = async (cart) => {
	const lineItems = cart.lines?.nodes || [];
	const totalPrice = cart.cost.subtotalAmount;
	const checkoutUrl = cart.checkoutUrl;
	const cartId = cart.id;

	let totalQuantity = 0,
		totalProgress = 0,
		totalItemsCount = 0;

	const freeGifts = {};
	let items = {};
	const bundles = {};
	const lineItemsLen = lineItems.length;
	let bundle_products_included = [];

	for (let i = 0; i < lineItemsLen; i++) {
		const item = lineItems[i];
		const variant = item.variant;

		if (!variant) continue;

		const product = variant.product;
		const discounts = [];
		let itemWeight = variant.weightUnit === "GRAMS" ? parseFloat(variant.weight / 1000) : variant.weight;
		const _isAddOn = isAddOn(product);

		if (product.productType === "Gift Card" && lineItemsLen > 1 || itemWeight === 0.001) {
			itemWeight = 0;
		}

		const price = +variant.price.amount;
		const compareAtPrice = Math.max(+variant.compareAtPrice?.amount || 0, price) ?? price;

		const totalPrice = (compareAtPrice ?? price) * item.quantity;
		let totalDiscountedPrice = price * item.quantity;

		const itemDiscounts = item.discountAllocations;
		const bundleId = item.attributes?.find((attr) => attr.key === "_bid")?.value;

		if (!_isAddOn) {
			totalQuantity += item?.quantity;
			totalProgress += itemWeight * item.quantity;
		}

		let itemDiscountsAmount = 0;

		if (itemDiscounts.length) {
			itemDiscounts.forEach(discount => {
				const amount = parseFloat(discount.discountedAmount?.amount);
				discounts.push({
					title: discount?.title ?? discount?.code ?? `- ${amount}`,
					amount,
				});
				if (amount) itemDiscountsAmount += amount;
			});
			totalDiscountedPrice -= itemDiscountsAmount;
		}

		if (product.freeGifts && product.freeGifts.references) {
			product.freeGifts.references.nodes.map(gift => {
				const giftVariant = gift.variants.nodes[0];
				const freeGift = {
					title: gift.title,
					id: gift.id,
					image: giftVariant.image,
					variant: {
						id: giftVariant.id,
						price: parseFloat(giftVariant.price.amount),
					},
					quantity: item.quantity,
					disableInputs: true,
				};
				if (freeGifts[giftVariant.id]) {
					freeGifts[giftVariant.id].quantity += item.quantity;
				} else {
					freeGifts[giftVariant.id] = freeGift;
				}
			});
		}

		const isFreeGift = compareAtPrice > 0 && price === 0;

		const cartItem = {
			id: product.id,
			tempId: item.id,
			title: product.title,
			handle: product.handle,
			quantity: item.quantity,
			image: { ...variant.image, altText: product.title },
			attributes: item.attributes || [],
			discounts,
			__quantity: 1,
			inBundle: Boolean(bundleId) || product?.inBundle,
			type: product.productType,
			oneTime: Boolean(item.attributes?.find(attr => attr.key === "_oneTime") || false),
			isFreeGift: isFreeGift || Boolean(item.attributes?.find(attr => attr.key === "_freeGift") || false),
			sellingPlan: item.sellingPlanAllocation?.sellingPlan,
			variant: {
				available: variant.available,
				sellingPlanAllocations: item.variant.sellingPlanAllocations?.nodes ?? [],
				id: variant.id,
				sku: variant.sku,
				title: variant.title,
				price,
				totalPrice,
				compareAtPrice,
				weight: itemWeight,
				discountedPrice: +(totalDiscountedPrice / item.quantity).toFixed(2),
				totalDiscountedPrice,
			},
		};

		if (bundleId) {
			if (!bundles[bundleId]) {
				let bundleInfo = null;
				const sanityBundleId = item.attributes?.find((attr) => attr.key === "_sdid")?.value;

				try {
					const response = await fetch("/.netlify/functions/fetch-bundle", {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							bundleId: sanityBundleId,
						})
					});

					const data = await response.json();

					if (data.bundle) bundleInfo = data.bundle;
				} catch (error) {
					console.log(error);
				}

				if (!bundleInfo) continue;

				bundle_products_included = getBundleSubproductLines(bundleInfo);

				const subProductRef = bundle_products_included.find((subp) => {
					const subProductId = subp._id.split("__")[0];

					return +subProductId === +extractIdFromGid(cartItem.id);
				});

				let qty = cartItem.quantity / +(subProductRef.__quantity);

				bundles[bundleId] = {
					...bundleInfo,
					quantity: qty,
					tempId: bundleInfo.id,
					subproducts: [],
				};

				const bundle = bundles[bundleId];

				const price = bundle.variant.price;
				const compareAtPrice = Math.max(bundle.variant.compareAtPrice || 0, price) ?? price;

				const totalPrice = (compareAtPrice ?? price) * qty;
				let totalDiscountedPrice = price * qty;

				bundle.variant = {
					...bundle.variant,
					totalPrice,
					totalDiscountedPrice,
					discountedPrice: +(totalDiscountedPrice / qty).toFixed(2),
				};
			}

			// if the attribute is missing from subproduct then add it manually
			if (bundle_products_included.length) {
				const subproduct = bundle_products_included.find((sp) => {
					const subProductId = sp._id.split("__")[0];

					return +subProductId === +extractIdFromGid(cartItem.id);
				});

				if (subproduct) {
					cartItem.__quantity = +subproduct.__quantity;
				}
			}

			const currBundle = bundles[bundleId];

			totalItemsCount += cartItem.quantity;
			currBundle.subproducts.push(cartItem);

			continue;
		}

		totalItemsCount += cartItem.quantity;
		items[variant.id] = cartItem;
	}

	const mappedResult = {
		id: cartId,
		items: Object.assign(items, bundles),
		attributes: cart.attributes,
		totalPrice: parseFloat(totalPrice?.amount),
		checkoutUrl,
		totalQuantity,
		totalProgress,
		totalItemsCount,
		freeGifts: Object.values(freeGifts),
		discountCodes: cart.discountCodes.map(discount => discount.code),
		allGiftCards: lineItems?.length > 0 && lineItems.every((item) => item.variant?.product?.productType === "Gift Card"),
		loading: false,
		error: false,
	};
	console.groupEnd();

	return mappedResult;
};

// Function to separate the cart items into core and side products (add-ons)
export const getCartItemsSeparated = (items) => {
	let coreProducts = [], sideProducts = [], bundleProducts = [];

	items.forEach(item => {
		// Add-ons are always side products as well as items with weight 1g (0.001kg)
		// Type is not available for the account cart, so we use weightInGrams
		if (item.duplicates) {
			item.duplicates.forEach(duplicate => {
				if (duplicate.discountedPrice !== item.discountedPrice &&
					(duplicate.discountedPrice === 0 || item.discountedPrice === 0)
				) {
					(isAddOn(item))
						? sideProducts.push(duplicate)
						: coreProducts.push(duplicate);
				}
			});
		}

		if (isAddOn(item))
			return sideProducts.push(item);

		if (isBundleWithSubProducts(item)) {
			const subproducts = item.subproducts?.length ? item.subproducts : getBundleSubproductLines(item);

			for (let i = 0; i < subproducts.length; i++) {
				const subprod = subproducts[i];

				if (isAddOn(subprod)) {
					let existingAddOn = sideProducts.find((product) => product.id === subprod.id);

					if (existingAddOn) {
						existingAddOn = {
							...existingAddOn,
							quantity: existingAddOn.quantity + (subprod.quantity || 1),
						};

						sideProducts = sideProducts.map((product) => {
							return product.id === existingAddOn.id ? existingAddOn : product;
						});
					} else {
						sideProducts.push(subprod);
					}
				}
			}

			return bundleProducts.push(item);
		}

		coreProducts.push(item);
	});

	sideProducts.sort((a, b) => {
		if (
			Number.isFinite(a?.variant?.price) &&
			Number.isFinite(b?.variant?.price)
		) {
			return a.variant.price - b.variant.price;
		}

		if (
			Number.isFinite(a?.basePrice) &&
			Number.isFinite(b?.basePrice)
		) {
			return a.basePrice - b.basePrice;
		}
	});

	return {
		coreProducts,
		bundleProducts,
		sideProducts,
	};
};

// When a side product is removed, add it to the session storage to prevent it from showing again
export const onSideProductRemoved = (variantId) => {
	// Get the removed upsells from local storage
	const upsellStorage = JSON.parse(sessionStorage.getItem(upsellsRemovedKey)) || [];
	// Add the current upsells (uniques) to the list
	sessionStorage.setItem(upsellsRemovedKey, JSON.stringify([...new Set([...upsellStorage, `${variantId}`])]));
};

export const calculateBowlsPrice = (items) => items.reduce((acc, item) => {
	const hasWeight = item?.variant?.weight || item?.weightInGrams;
	if (!item?.variant || !hasWeight) return acc;

	return acc + item.quantity * item.variant.price;
}, 0);

export const isOneTimeCartItem = (item) => {
	if (!item) return false;
	if (item.oneTime || item.stock === "one-time" || item?.variant?.stock === "one-time") {
		return true;
	}

	if (Array.isArray(item.subproducts) && item.subproducts.length) {
		const subproducts = item.subproducts.filter((subproduct) => subproduct && !subproduct.isFreeGift);
		return subproducts.length > 0 && subproducts.every(isOneTimeCartItem);
	}

	return false;
};

export const isRecurringCartItem = (item) => Boolean(item) && !item?.isFreeGift && !isOneTimeCartItem(item);

export const hasRecurringCartItems = (items = []) => items.some(isRecurringCartItem);

export const isPureOneTimeCart = (items = []) => {
	const chargeableItems = items.filter((item) => item && !item?.isFreeGift);
	return chargeableItems.length > 0 && chargeableItems.every(isOneTimeCartItem);
};

export const getCartItemWeight = (item) =>
	item?.variant?.weight || (item?.weightInGrams ? item.weightInGrams / 1000 : 0);

export const getCartItemBoxDiscountedTotal = (item, discount = 0) => {
	if (!item?.variant) return 0;

	const weight = getCartItemWeight(item);
	if (!weight) {
		return item?.variant?.totalDiscountedPrice || 0;
	}

	const linePrice = item.quantity * (item.variant.price - (discount * weight));
	return linePrice > 0 ? linePrice : 0;
};

// Calculate the price of all items in the cart for the active box
export const calculateTotalBoxPrice = (items, discount) => {
	return items.reduce((acc, item) => {
		if (!item?.variant) return acc;

		const weight = item?.variant?.weight || (item?.weightInGrams ? item.weightInGrams / 1000 : 0);

		if (weight) {
			const boxDiscount = discount * weight;
			return acc + item.quantity * (item.variant.price - boxDiscount);
		}

		// If item has no weight, use totalDiscountedPrice
		return acc + item?.variant?.totalDiscountedPrice;
	}, 0);
};

// Calculate total for all items in the cart without weight or that are one-time purchases
export const calculateTotalNoWeightPrice = (items) => items.reduce((acc, item) => {
	if (!item?.variant || item?.variant?.weight || item?.variant?.sellingPlanAllocations?.length) return acc;

	return acc + item.variant.totalDiscountedPrice;
}, 0);

// Function to find the closest box based on totalWeight
export const findClosestBox = (boxes, totalWeight) => {
	const sortedBoxes = boxes.slice().sort((a, b) => b.size.value - a.size.value);

	const closestBox = sortedBoxes.find(box => box.size.value <= totalWeight);

	return closestBox ?? sortedBoxes[sortedBoxes.length - 1];
};

export const getHighestBoxPrice = (boxes) => Math.max(...boxes.map(box => box.price));

export const getBoxPriceDetails = (activeBox, boxes, discountPercentage, shipping) => {
	const highestBoxPrice = getHighestBoxPrice(boxes);
	const boxValue = activeBox?.size?.value || 0;
	const totalBoxPrice = boxValue * highestBoxPrice;
	const totalBowlsPrice = boxValue * activeBox?.price || 0;
	const shippingCost = boxValue <= 6 ? shipping : 0;
	const totalOriginalPrice = totalBoxPrice + shippingCost;
	const discountedPrice = totalBowlsPrice + shippingCost;

	let firstOrderDiscount = 0;
	if (discountPercentage) {
		firstOrderDiscount = Math.ceil((discountPercentage / 100 * totalBowlsPrice) * 100) / 100;
	}
	const totalDiscountedPrice = discountedPrice - firstOrderDiscount;

	const savings = totalOriginalPrice - totalDiscountedPrice;

	return {
		highestBoxPrice,
		totalBoxPrice,
		totalBowlsPrice,
		savings,
		totalOriginalPrice,
		totalDiscountedPrice,
		firstOrderDiscount
	};
};
